import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatMoney, formatPct } from "@/lib/apiClient";
import { ArrowRight, ShieldWarning } from "@phosphor-icons/react";

const HEADERS = {
  crypto: {
    title: "Crypto",
    lede: "Sovereign digital reserves — priced against live institutional oracles.",
    disclosure:
      "Crypto assets are highly volatile and traded 24/7. You may lose your entire investment. Not FDIC or FSCS insured.",
  },
  gold: {
    title: "Gold",
    lede: "Allocated London Good Delivery bars, vaulted with quarterly audit attestations.",
    disclosure:
      "Physical gold pricing responds to macroeconomic and geopolitical conditions. Storage and insurance fees apply on physical redemption.",
  },
  stocks: {
    title: "Digital Stocks",
    lede: "On-chain wrappers tracking listed equities — fractional units, T+0 settlement.",
    disclosure:
      "Digital equity wrappers track underlying listed shares but are not direct shareholdings. Corporate actions may be applied on a best-effort basis.",
  },
  spacex: {
    title: "SpaceX Frontier",
    lede: "Structured, private access to SpaceX and Starlink adjacent opportunities.",
    disclosure:
      "Private frontier positions are illiquid, long-duration, and restricted to accredited investors. Total loss of capital is possible.",
  },
};

export default function Marketplace() {
  const { category } = useParams();
  const meta = HEADERS[category] || HEADERS.crypto;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/assets?category=${category}`)
      .then((r) => setAssets(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div data-testid={`marketplace-${category}`} className="text-white">
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <div className="overline mb-4">Marketplace · {meta.title}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 max-w-3xl">
            {meta.title}
            <span className="text-[#E6C998]">.</span>
          </h1>
          <p className="text-neutral-400 max-w-2xl leading-relaxed text-lg">
            {meta.lede}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        {loading ? (
          <div className="text-neutral-500 text-sm">Loading assets…</div>
        ) : assets.length === 0 ? (
          <div className="text-neutral-500 text-sm">No assets available.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map((a) => (
              <Link
                to={`/asset/${a.symbol}`}
                key={a.symbol}
                data-testid={`asset-card-${a.symbol}`}
                className="lux-card group relative overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.name}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-[1.04] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/40 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="overline mb-1">{a.symbol}</div>
                      <div className="font-display text-xl">{a.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="num text-white text-lg">
                        {formatMoney(a.price, { max: a.price > 1000 ? 0 : 2 })}
                      </div>
                      <div
                        className={`num text-xs ${
                          a.change_24h >= 0 ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {formatPct(a.change_24h)} · 24h
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-6 line-clamp-3">
                    {a.description}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-neutral-500">
                      Min · <span className="text-white num">${a.min_investment}</span>
                    </div>
                    <div className="text-neutral-500">
                      Target APY ·{" "}
                      <span className="text-[#E6C998] num">
                        {a.expected_apy}%
                      </span>
                    </div>
                    <div className="text-neutral-500">
                      Risk · <span className="text-white">{a.risk}</span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-4 right-4 text-[#E6C998] opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight size={20} weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 border border-[#E6C998]/20 bg-[#141414] p-6 flex gap-4">
          <ShieldWarning
            size={28}
            weight="duotone"
            className="text-[#E6C998] shrink-0 mt-1"
          />
          <div>
            <div className="overline mb-2">Category disclosure</div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              {meta.disclosure} Please read our full{" "}
              <Link to="/risk" className="text-[#E6C998] underline">
                risk disclosure
              </Link>{" "}
              before investing.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
